import React, { useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import { Heart, MessageCircle, Play, Trophy, Trash2, Clock, Share2, Twitter, Copy, Check, MessageSquare } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AuthModal from "../components/AuthModal";
import { api, formatError, resolveAsset } from "../lib/api";
import { useAuth } from "../lib/auth";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function ClipCard({ clip, active, onSelect }) {
  const thumb = clip.thumb_url ? resolveAsset(clip.thumb_url) : null;
  return (
    <button
      onClick={() => onSelect(clip)}
      data-testid={`clip-card-${clip.id}`}
      className={`group text-left rounded-2xl overflow-hidden border transition-all relative ${
        active
          ? "border-red-500/70 shadow-[0_0_22px_rgba(230,0,0,0.35)]"
          : "border-white/10 hover:border-red-500/40 hover:shadow-[0_0_20px_rgba(230,0,0,0.25)]"
      }`}
    >
      <div className="aspect-video bg-black/60 relative overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            loading="lazy"
            alt=""
            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-600 text-xs font-mono">NO PREVIEW</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[10px] font-mono">
          <span className="text-zinc-400 tracking-widest uppercase">{timeAgo(clip.created_at)}</span>
          <span className="flex items-center gap-2 text-red-200">
            <span className="flex items-center gap-1"><Heart size={10} /> {clip.like_count || 0}</span>
            <span className="flex items-center gap-1"><MessageCircle size={10} /> {clip.comment_count || 0}</span>
          </span>
        </div>
        <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="h-12 w-12 rounded-full bg-red-600/80 border border-red-300/40 grid place-items-center">
            <Play size={18} className="text-white ml-0.5" fill="currentColor" />
          </div>
        </div>
        {clip.featured && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] tracking-widest uppercase font-mono px-1.5 py-0.5 bg-yellow-400/20 border border-yellow-400/40 text-yellow-200 rounded-full">
            <Trophy size={8} fill="currentColor" /> FEATURED
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="font-display font-bold text-sm text-white truncate">{clip.title}</div>
      </div>
    </button>
  );
}

function CommentThread({ clipId }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const load = async () => {
    try {
      const { data } = await api.get(`/clips/${clipId}/comments`);
      setComments(data);
    } catch {}
  };
  useEffect(() => { if (clipId) load(); /* eslint-disable-next-line */ }, [clipId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to comment");
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/clips/${clipId}/comments`, { body: body.trim() });
      setComments((prev) => [data, ...prev]);
      setBody("");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/clips/${clipId}/comments/${c.id}`);
      setComments((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="glass-dark rounded-2xl p-5">
      <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
        <MessageCircle size={16} className="text-red-400" /> Comments
        <span className="text-[10px] font-mono text-zinc-500">({comments.length})</span>
      </h3>
      {user ? (
        <form onSubmit={submit} className="flex gap-2 mb-4">
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share your thoughts..."
            disabled={sending}
            maxLength={500}
            data-testid="clip-comment-input"
            className="flex-1 bg-black/50 border border-white/10 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-red-500 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={!body.trim() || sending}
            data-testid="clip-comment-submit"
            className="btn-liquid !py-2 !px-4 !text-xs"
          >
            Post
          </button>
        </form>
      ) : (
        <div
          data-testid="clip-comment-signin-cta"
          className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/5 px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="text-xs text-zinc-300">
            Sign in to drop a comment on this clip.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
              data-testid="clip-comment-login-btn"
              className="btn-ghost-glass !py-1.5 !px-3 !text-[11px]"
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => { setAuthMode("register"); setAuthOpen(true); }}
              data-testid="clip-comment-register-btn"
              className="btn-liquid !py-1.5 !px-3 !text-[11px]"
            >
              Register
            </button>
          </div>
          <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />
        </div>
      )}
      {comments.length === 0 ? (
        <div className="text-xs text-zinc-500 py-3 text-center">Be the first to drop a comment.</div>
      ) : (
        <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
          {comments.map((c) => (
            <div key={c.id} data-testid={`clip-comment-${c.id}`} className="flex items-start gap-3 group">
              {c.user_avatar_url ? (
                <img src={resolveAsset(c.user_avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-white/10" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-zinc-800 grid place-items-center text-[11px] font-bold">
                  {(c.username || "?").slice(0, 2).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="font-semibold text-white">{c.username}</span>
                  <span className="text-zinc-500 font-mono">{timeAgo(c.created_at)}</span>
                </div>
                <div className="text-sm text-zinc-300 break-words mt-0.5">{c.body}</div>
              </div>
              {user?.role === "admin" && (
                <button
                  onClick={() => removeComment(c)}
                  data-testid={`clip-comment-delete-${c.id}`}
                  className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                  title="Delete comment"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlayOfTheWeekPage() {
  const { user } = useAuth();
  const [featured, setFeatured] = useState(null);
  const [archive, setArchive] = useState([]);
  const [current, setCurrent] = useState(null);
  const [likeBusy, setLikeBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const videoRef = useRef(null);

  // Always land at the top of the page — otherwise React Router keeps the
  // scroll position from the previous page, which makes the user start in
  // the middle of the comments section instead of the video.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  const load = async () => {
    try {
      const [f, all] = await Promise.all([
        api.get("/clips/featured"),
        api.get("/clips"),
      ]);
      setFeatured(f.data);
      setArchive(all.data || []);
      setCurrent((c) => c || f.data || (all.data || [])[0] || null);
    } catch (e) {
      // Endpoint may return null when empty
    }
  };
  useEffect(() => { load(); }, []);

  // Reset + reload video on current change
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [current?.id]);

  const toggleLike = async () => {
    if (!user) return toast.error("Sign in to like clips");
    if (!current) return;
    setLikeBusy(true);
    try {
      if (current.viewer_has_liked) {
        const { data } = await api.delete(`/clips/${current.id}/like`);
        setCurrent((c) => c && ({ ...c, viewer_has_liked: false, like_count: data.like_count }));
      } else {
        const { data } = await api.post(`/clips/${current.id}/like`);
        setCurrent((c) => c && ({ ...c, viewer_has_liked: true, like_count: data.like_count }));
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLikeBusy(false);
    }
  };

  const archiveSorted = useMemo(
    () => archive.filter((c) => !current || c.id !== current.id),
    [archive, current],
  );

  const shareLinks = useMemo(() => {
    if (!current || typeof window === "undefined") return null;
    const origin = window.location.origin;
    // Deep link so opening the URL loads this specific clip.
    const shareUrl = `${origin}/play-of-the-week?clip=${current.id}`;
    const text =
      `🎮 ${current.title}` +
      (current.featured ? " · Play of the Week on Spicy Community" : " · on Spicy Community");
    const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    const discord = `${text}\n${shareUrl}`;
    return { shareUrl, text, tweet, discord };
  }, [current]);

  const copyLink = async () => {
    if (!shareLinks) return;
    try {
      await navigator.clipboard.writeText(shareLinks.shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const copyDiscord = async () => {
    if (!shareLinks) return;
    try {
      await navigator.clipboard.writeText(shareLinks.discord);
      toast.success("Discord snippet copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const nativeShare = async () => {
    if (!shareLinks) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: current.title,
          text: shareLinks.text,
          url: shareLinks.shareUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  // Deep-link: if ?clip=<id> is in the URL, select that clip after load.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("clip");
    if (!wanted) return;
    const match = archive.find((c) => c.id === wanted);
    if (match) setCurrent(match);
  }, [archive]);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-200">
      <Toaster position="top-right" theme="dark" />
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Community Highlights</div>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">
          Play of the <span className="text-red-500">Week</span>
        </h1>
        <p className="text-zinc-400 mt-3 max-w-2xl text-sm">
          The sickest moments from our community, picked and published by admins every week.
        </p>
      </section>

      {!current ? (
        <section className="max-w-3xl mx-auto px-6 pb-24">
          <div className="glass-dark rounded-2xl p-12 text-center text-zinc-500">
            <Trophy size={32} className="mx-auto mb-3 text-zinc-700" />
            <div className="text-lg font-display font-bold mb-1">No clips yet</div>
            <div className="text-xs">Check back soon — admins will drop the first Play of the Week.</div>
          </div>
        </section>
      ) : (
        <>
          <section className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="lg:col-span-2 glass-dark rounded-2xl overflow-hidden"
              >
                <div className="aspect-video bg-black">
                  {current.source_type === "youtube" && current.youtube_id ? (
                    <iframe
                      key={current.id}
                      src={`https://www.youtube.com/embed/${current.youtube_id}?rel=0&modestbranding=1`}
                      title={current.title}
                      data-testid="clip-player-youtube"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="w-full h-full border-0"
                    />
                  ) : (
                    <video
                      ref={videoRef}
                      key={current.id}
                      controls
                      playsInline
                      preload="metadata"
                      src={resolveAsset(current.video_url)}
                      poster={current.thumb_url ? resolveAsset(current.thumb_url) : undefined}
                      data-testid="clip-player"
                      className="w-full h-full object-contain bg-black"
                    />
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      {current.featured && (
                        <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.25em] font-mono px-2 py-0.5 mb-2 rounded-full bg-yellow-400/20 border border-yellow-400/40 text-yellow-200">
                          <Trophy size={9} fill="currentColor" /> Play of the Week
                        </div>
                      )}
                      <h2 className="font-display font-bold text-2xl text-white">{current.title}</h2>
                      {current.description && (
                        <p className="text-sm text-zinc-400 mt-2 whitespace-pre-line">{current.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-[11px] font-mono text-zinc-500 uppercase tracking-widest">
                        <span className="flex items-center gap-1"><Clock size={11} /> {timeAgo(current.created_at)}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2 self-start">
                      <button
                        onClick={toggleLike}
                        disabled={likeBusy}
                        data-testid="clip-like-btn"
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all ${
                          current.viewer_has_liked
                            ? "bg-red-500/20 border-red-500/50 text-red-200"
                            : "glass-dark text-zinc-300 hover:border-red-500/40"
                        }`}
                      >
                        <Heart size={14} fill={current.viewer_has_liked ? "currentColor" : "none"} />
                        <span className="font-mono font-bold text-xs">{current.like_count || 0}</span>
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setShareOpen((v) => !v)}
                          data-testid="clip-share-btn"
                          aria-label="Share clip"
                          aria-expanded={shareOpen}
                          className={`flex items-center gap-1.5 px-4 py-2 rounded-full border transition-all ${
                            shareOpen
                              ? "bg-red-500/20 border-red-500/50 text-red-200"
                              : "glass-dark text-zinc-300 hover:border-red-500/40"
                          }`}
                        >
                          <Share2 size={14} />
                          <span className="font-mono font-bold text-xs hidden sm:inline">Share</span>
                        </button>
                        {shareOpen && shareLinks && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setShareOpen(false)}
                              aria-hidden
                            />
                            <div
                              data-testid="clip-share-popover"
                              className="fixed inset-x-4 bottom-4 z-50 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-72 glass-red-glow rounded-2xl p-4 shadow-2xl"
                            >
                              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-300 mb-2">
                                Share this clip
                              </div>
                              <div className="flex items-stretch gap-2 mb-3">
                                <input
                                  readOnly
                                  value={shareLinks.shareUrl}
                                  onFocus={(e) => e.target.select()}
                                  data-testid="clip-share-url"
                                  className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded-lg px-2.5 text-[11px] text-zinc-200 focus:outline-none focus:border-red-500 font-mono"
                                />
                                <button
                                  onClick={copyLink}
                                  data-testid="clip-share-copy"
                                  aria-label="Copy link"
                                  className="btn-liquid !py-2 !px-3 !text-[11px]"
                                >
                                  {copied ? <Check size={12} /> : <Copy size={12} />}
                                </button>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                <a
                                  href={shareLinks.tweet}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={() => setShareOpen(false)}
                                  data-testid="clip-share-twitter"
                                  className="btn-ghost-glass !text-[10px] !py-2 justify-center !gap-1"
                                >
                                  <Twitter size={12} /> X
                                </a>
                                <button
                                  onClick={copyDiscord}
                                  data-testid="clip-share-discord"
                                  className="btn-ghost-glass !text-[10px] !py-2 justify-center !gap-1"
                                >
                                  <MessageSquare size={12} /> Discord
                                </button>
                                <button
                                  onClick={nativeShare}
                                  data-testid="clip-share-native"
                                  className="btn-ghost-glass !text-[10px] !py-2 justify-center"
                                >
                                  More…
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <CommentThread clipId={current.id} />
            </div>
          </section>

          {archiveSorted.length > 0 && (
            <section className="max-w-7xl mx-auto px-6 mt-14 pb-16">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display font-bold text-2xl tracking-tight">More clips</h3>
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                  Archive · {archiveSorted.length}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {archiveSorted.map((c) => (
                  <ClipCard key={c.id} clip={c} active={current?.id === c.id} onSelect={setCurrent} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}
